import { format } from 'date-fns';

import type { components } from '@/types/apiSchema';

type SourcingAllowance =
  components['schemas']['AgentSourcingAllowanceResponse'];

export interface MoreCandidatesPreview {
  /** The most new candidates the round adds; 0 when the allowance is used up and no round can start. */
  size: number;
  text: string;
}

const day = (stamp: string) => format(new Date(stamp), 'MMMM d');

const usedUp = ({ limit, periodEndsAt }: SourcingAllowance): string => {
  if (!limit) return 'Your plan doesn’t include finding new candidates.';
  if (periodEndsAt) {
    return `You’ve used all ${limit} new candidates of your allowance; it resets on ${day(periodEndsAt)}.`;
  }
  return `You’ve used the ${limit} new candidates your plan includes.`;
};

/**
 * What another round adds and uses of the workspace's sourcing allowance, worded as the Finlay agent's preview
 * (ai-services propose_more_candidates), so the button and the chat say the same.
 */
export const moreCandidatesPreview = (
  allowance: SourcingAllowance,
): MoreCandidatesPreview => {
  const size = allowance.nextRoundSize;
  if (size === 0) return { size, text: usedUp(allowance) };
  const round = `Finlay searches again for up to ${size} new candidates and leaves out everyone already on the job.`;
  if (allowance.isUnlimited) return { size, text: round };
  const until = allowance.periodEndsAt
    ? ` until ${day(allowance.periodEndsAt)}`
    : ' on your plan';
  const uses =
    size === allowance.left
      ? `That’s the last ${size} of your allowance${until}.`
      : `That uses up to ${size} of your allowance: you have ${allowance.left} of ${allowance.limit} left${until}.`;
  return { size, text: `${round} ${uses}` };
};
